import { gatewayApi } from './apiService';

const statisticsService = {
  /**
   * Получить статистику добавленных талантов по менеджерам
   * @param {Object} params - Параметры запроса
   * @param {string} params.startDate - Начальная дата (ISO format)
   * @param {string} params.endDate - Конечная дата (ISO format)
   * @param {string[]} params.talent_types - Типы талантов
   * @param {string[]} params.manager_ids - ID менеджеров
   * @param {number} params.limit - Лимит записей
   * @param {number} params.offset - Смещение
   * @param {string} params.period - Период группировки (day, week, month)
   * @returns {Promise<Object>} Статистика
   */
  async getManagerTalentsAdded(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/talents-added', { params });
      // API Gateway возвращает: { success, message, data: { success, data: [...] }, code }
      // Извлекаем вложенный data
      console.log('Raw response from gateway:', response.data);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager talents statistics:', error);
      throw error;
    }
  },

  /**
   * Получить суммарную статистику по всем менеджерам (без пагинации, для строки TOTAL в матрице)
   * @param {Object} params - Параметры запроса (startDate, endDate, talent_types, manager_ids, period)
   * @returns {Promise<Object>} { grand_total, by_period: [{ period, count }] }
   */
  async getManagerTalentsTotals(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/talents-totals', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager talents totals:', error);
      throw error;
    }
  },

  /**
   * Получить детальную статистику по конкретному менеджеру
   * @param {string} managerId - ID менеджера
   * @param {Object} params - Параметры запроса
   * @param {string} params.startDate - Начальная дата (ISO format)
   * @param {string} params.endDate - Конечная дата (ISO format)
   * @param {string[]} params.talent_types - Типы талантов
   * @returns {Promise<Object>} Детальная статистика
   */
  async getManagerDetails(managerId, params = {}) {
    try {
      const response = await gatewayApi.get(`/talent-service/statistics/managers/${managerId}/details`, { params });
      // API Gateway возвращает: { success, message, data: { success, data: {...} }, code }
      // Извлекаем вложенный data
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager details:', error);
      throw error;
    }
  },

  /**
   * Кастомный отчет — конструктор запросов
   * @param {Object} queryConfig - Конфигурация отчета
   * @param {string[]} queryConfig.dimensions - Измерения для группировки
   * @param {string[]} queryConfig.metrics - Метрики (пока ['count'])
   * @param {Object} queryConfig.filters - Фильтры
   * @param {number} queryConfig.limit - Лимит строк
   * @param {number} queryConfig.offset - Смещение
   * @returns {Promise<Object>} Результат отчета
   */
  async getCustomReport(queryConfig = {}) {
    try {
      const response = await gatewayApi.post('/talent-service/statistics/custom-report', queryConfig);
      // API Gateway возвращает: { success, message, data: { success, data: { rows, totals, ... }, pagination }, code }
      // Извлекаем данные из вложенной структуры
      const nestedData = response.data.data?.data || response.data.data || response.data;
      const pagination = response.data.data?.pagination || response.data.pagination;

      return {
        ...nestedData,
        pagination
      };
    } catch (error) {
      console.error('Error fetching custom report:', error);
      throw error;
    }
  },

  /**
   * Сравнение двух периодов
   * @param {Object} params - Параметры сравнения
   * @param {string} params.period1_start
   * @param {string} params.period1_end
   * @param {string} params.period2_start
   * @param {string} params.period2_end
   * @param {string} params.period1_label (optional)
   * @param {string} params.period2_label (optional)
   * @param {string[]} params.talent_types (optional)
   * @param {string[]} params.manager_ids (optional)
   * @returns {Promise<Object>} { period1, period2, comparison }
   */
  async getPeriodComparison(params = {}) {
    try {
      const response = await gatewayApi.post('/talent-service/statistics/period-comparison', params);
      // API Gateway возвращает: { success, message, data: { success, data: {...} }, code }
      // Извлекаем вложенный data.data
      return response.data.data?.data || response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching period comparison:', error);
      throw error;
    }
  },

  /**
   * Получить статистику добавленных видео по менеджерам
   * @param {Object} params - Параметры запроса
   * @param {string} params.startDate - Начальная дата (ISO format)
   * @param {string} params.endDate - Конечная дата (ISO format)
   * @param {string[]} params.manager_ids - ID менеджеров
   * @param {number} params.limit - Лимит записей
   * @param {number} params.offset - Смещение
   * @param {string} params.period - Период группировки (day, week, month)
   * @returns {Promise<Object>} Статистика видео
   */
  async getManagerVideosAdded(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/videos-added', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager videos statistics:', error);
      throw error;
    }
  },

  /**
   * Получить суммарную статистику по видео всех менеджеров (без пагинации, для строки TOTAL в матрице)
   * @param {Object} params - Параметры запроса (startDate, endDate, manager_ids, period)
   * @returns {Promise<Object>} { grand_total, by_period: [{ period, count }] }
   */
  async getManagerVideosTotals(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/videos-totals', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager videos totals:', error);
      throw error;
    }
  },

  /**
   * Получить детальную статистику по видео конкретного менеджера
   * @param {string} managerId - ID менеджера
   * @param {Object} params - Параметры запроса
   * @param {string} params.startDate - Начальная дата (ISO format)
   * @param {string} params.endDate - Конечная дата (ISO format)
   * @returns {Promise<Object>} Детальная статистика
   */
  async getManagerVideoDetails(managerId, params = {}) {
    try {
      const response = await gatewayApi.get(`/talent-service/statistics/managers/${managerId}/videos-details`, { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager video details:', error);
      throw error;
    }
  },

  /**
   * Сравнение двух периодов по видео
   * @param {Object} params - Параметры сравнения
   * @param {string} params.period1_start
   * @param {string} params.period1_end
   * @param {string} params.period2_start
   * @param {string} params.period2_end
   * @param {string} params.period1_label (optional)
   * @param {string} params.period2_label (optional)
   * @param {string[]} params.manager_ids (optional)
   * @returns {Promise<Object>} { period1, period2, comparison }
   */
  async getPeriodComparisonVideos(params = {}) {
    try {
      const response = await gatewayApi.post('/talent-service/statistics/period-comparison-videos', params);
      return response.data.data?.data || response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching period comparison videos:', error);
      throw error;
    }
  },

  async getManagerYoutubeAdded(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/youtube-added', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager youtube statistics:', error);
      throw error;
    }
  },

  async getManagerYoutubeTotals(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/youtube-totals', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager youtube totals:', error);
      throw error;
    }
  },

  async getManagerYoutubeDetails(managerId, params = {}) {
    try {
      const response = await gatewayApi.get(`/talent-service/statistics/managers/${managerId}/youtube-details`, { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager youtube details:', error);
      throw error;
    }
  },

  async getPeriodComparisonYoutube(params = {}) {
    try {
      const response = await gatewayApi.post('/talent-service/statistics/period-comparison-youtube', params);
      return response.data.data?.data || response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching period comparison youtube:', error);
      throw error;
    }
  },

  async getManagerPortraitAdded(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/portrait-added', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager portrait statistics:', error);
      throw error;
    }
  },

  async getManagerPortraitTotals(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/portrait-totals', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager portrait totals:', error);
      throw error;
    }
  },

  async getManagerPortraitDetails(managerId, params = {}) {
    try {
      const response = await gatewayApi.get(`/talent-service/statistics/managers/${managerId}/portrait-details`, { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager portrait details:', error);
      throw error;
    }
  },

  async getPeriodComparisonPortrait(params = {}) {
    try {
      const response = await gatewayApi.post('/talent-service/statistics/period-comparison-portrait', params);
      return response.data.data?.data || response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching period comparison portrait:', error);
      throw error;
    }
  },

  /**
   * Получить статистику hired по менеджерам
   * @param {Object} params - Параметры запроса
   * @returns {Promise<Object>} Статистика hired
   */
  async getManagerHiredStats(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/hired', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager hired statistics:', error);
      throw error;
    }
  },

  /**
   * Получить суммарную статистику hired по всем менеджерам
   * @param {Object} params - Параметры запроса
   * @returns {Promise<Object>} { grand_total, by_period }
   */
  async getManagerHiredTotals(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/hired-totals', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager hired totals:', error);
      throw error;
    }
  },

  /**
   * Получить детальную статистику hired по конкретному менеджеру
   * @param {string} managerId - ID менеджера
   * @param {Object} params - Параметры запроса
   * @returns {Promise<Object>} Детальная статистика
   */
  async getManagerHiredDetails(managerId, params = {}) {
    try {
      const response = await gatewayApi.get(`/talent-service/statistics/managers/${managerId}/hired-details`, { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager hired details:', error);
      throw error;
    }
  },

  /**
   * Сравнение двух периодов по hired
   * @param {Object} params - Параметры сравнения
   * @returns {Promise<Object>} Данные сравнения
   */
  async getPeriodComparisonHired(params = {}) {
    try {
      const response = await gatewayApi.post('/talent-service/statistics/period-comparison-hired', params);
      return response.data.data?.data || response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching period comparison hired:', error);
      throw error;
    }
  },

  /**
   * Получить статистику started по менеджерам
   * @param {Object} params - Параметры запроса
   * @returns {Promise<Object>} Статистика started
   */
  async getManagerStartedStats(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/started', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager started statistics:', error);
      throw error;
    }
  },

  /**
   * Получить суммарную статистику started по всем менеджерам
   * @param {Object} params - Параметры запроса
   * @returns {Promise<Object>} { grand_total, by_period }
   */
  async getManagerStartedTotals(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/managers/started-totals', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager started totals:', error);
      throw error;
    }
  },

  /**
   * Получить детальную статистику started по конкретному менеджеру
   * @param {string} managerId - ID менеджера
   * @param {Object} params - Параметры запроса
   * @returns {Promise<Object>} Детальная статистика
   */
  async getManagerStartedDetails(managerId, params = {}) {
    try {
      const response = await gatewayApi.get(`/talent-service/statistics/managers/${managerId}/started-details`, { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager started details:', error);
      throw error;
    }
  },

  /**
   * Сравнение двух периодов по started
   * @param {Object} params - Параметры сравнения
   * @returns {Promise<Object>} Данные сравнения
   */
  async getPeriodComparisonStarted(params = {}) {
    try {
      const response = await gatewayApi.post('/talent-service/statistics/period-comparison-started', params);
      return response.data.data?.data || response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching period comparison started:', error);
      throw error;
    }
  },

  /**
   * Получить статистику онбординга: кто вышел, кто прошел, кто не прошел
   * @param {Object} params - startDate, endDate, manager_id
   * @returns {Promise<Object>} { data: { counts, lists, transitions_log } }
   */
  async getOnboardingStats(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/onboarding', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching onboarding stats:', error);
      throw error;
    }
  },

  /**
   * HR recruitment funnel dashboard (6 blocks)
   * @param {Object} params - month (YYYY-MM), profession_id, hr_user_id, talent_type
   * @returns {Promise<Object>} { filters, block1_funnel, block2_dropoff, block3_exits, block4_sessions, block5_candidates, block6_conversion }
   */
  async getHrRecruitmentStats(params = {}) {
    try {
      const response = await gatewayApi.get('/talent-service/statistics/hr-recruitment', { params });
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching HR recruitment stats:', error);
      throw error;
    }
  }
};

export default statisticsService;
